import React, { useCallback, useEffect, useState } from "react";
import { SelectList } from "../../../shared/inputs/SelectList";
import { Pill } from "../../../shared/text/Pill.styled";
import { useProject } from "../../../context/ProjectContext";
import { useBanner } from "../../../context/BannerContext";
import { useDatasources } from "../../../context/DatasourceContext";
import { useDrawerActions } from "../../../context/DrawerContext";
import { useCostModelLoader } from "../../../hooks/useCostModelLoader";
import { fetchCostModelsByProjectIdUncached } from "../../../../api/costModels";
import { costModelLabel } from "../../../../utils/format";
import { COST_MODEL_ENABLED } from "../../../../config/features";
import { CostModel } from "../../../../interfaces/CostModel";
import { COST_MODELS_COMING_SOON, COST_MODELS_EMPTY, COST_MODELS_LOAD_ERROR } from "../../../constants";

const CostModelPicker: React.FC = () => {
  const { selectedProject, selectedCostModel } = useProject();
  const { addDatasource } = useDatasources();
  const { closeDrawer } = useDrawerActions();
  const { loadCostModel } = useCostModelLoader();
  const { showBanner } = useBanner();
  const [costModels, setCostModels] = useState<CostModel[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const projectId = selectedProject?.id;

  useEffect(() => {
    if (!projectId) return;

    let isCurrent = true;

    const loadCostModels = async () => {
      setLoading(true);
      try {
        const result = await fetchCostModelsByProjectIdUncached(projectId);
        if (!result.success) throw result.error;
        const sorted = [...(result.data ?? [])].sort(
          (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
        );
        if (isCurrent) setCostModels(sorted);
      } catch (error) {
        if (isCurrent) showBanner(COST_MODELS_LOAD_ERROR, "error");
      } finally {
        if (isCurrent) setLoading(false);
      }
    };

    loadCostModels();

    return () => {
      isCurrent = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // See EstimatesList: the load decides whether a card is earned, and the loader owns the selection.
  const handleSelectCostModel = useCallback(
    async (costModel: CostModel) => {
      const { dataLoaded } = await loadCostModel(costModel);
      if (!dataLoaded) return;
      addDatasource("ediphi", "cost-model");
      closeDrawer();
    },
    [addDatasource, closeDrawer, loadCostModel]
  );

  return (
    <SelectList<CostModel>
      items={costModels.map((costModel) => ({
        id: costModel.id,
        label: costModelLabel(costModel),
        endAdornment: costModel.lock ? <Pill>Locked</Pill> : null,
        data: costModel,
      }))}
      onSelectItem={(costModel) => void handleSelectCostModel(costModel)}
      title="Cost Models"
      selectedItemId={selectedCostModel?.id ?? null}
      loading={loading}
      emptyMessage={COST_MODELS_EMPTY}
    />
  );
};

const CostModelList: React.FC = () =>
  COST_MODEL_ENABLED ? (
    <CostModelPicker />
  ) : (
    <SelectList<never>
      items={[]}
      onSelectItem={() => {}}
      title="Cost Models"
      searchPlaceholder="Search"
      emptyMessage={COST_MODELS_COMING_SOON}
    />
  );

export default CostModelList;
